'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface TimeSlot {
  id: number;
  time: string;
  available: boolean;
}

const SchedulingTool = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    fleetSize: '',
    location: '',
    notes: ''
  });

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const timeSlots: TimeSlot[] = [
    { id: 1, time: '9:00 AM', available: true },
    { id: 2, time: '10:30 AM', available: true },
    { id: 3, time: '12:00 PM', available: false },
    { id: 4, time: '1:30 PM', available: true },
    { id: 5, time: '3:00 PM', available: true },
    { id: 6, time: '4:30 PM', available: false }
  ];

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isHydrated) return;
    setSelectedDate(e.target.value);
  };

  const handleTimeSelect = (time: string) => {
    if (!isHydrated) return;
    setSelectedTime(time);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    if (!isHydrated) return;
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isHydrated) return;
    console.log('Scheduling request:', { ...formData, selectedDate, selectedTime });
  };

  return (
    <section className="py-16 md:py-24 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-headline text-foreground mb-4">
            Schedule Your Fleet Evaluation
          </h2>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            Book a convenient time for our certified appraisers to conduct an on-site assessment of your commercial vehicles.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Scheduling Form */}
          <div className="bg-card rounded-lg p-6 md:p-8 shadow-brand">
            <h3 className="text-2xl font-bold font-headline text-foreground mb-6">
              Request an Appointment
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Information */}
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-foreground mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
                    placeholder="John Smith"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-foreground mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
                      placeholder="john@company.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-foreground mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="fleetSize" className="block text-sm font-semibold text-foreground mb-2">
                      Fleet Size *
                    </label>
                    <select
                      id="fleetSize"
                      name="fleetSize"
                      value={formData.fleetSize}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
                    >
                      <option value="">Select size</option>
                      <option value="1-5">1-5 vehicles</option>
                      <option value="6-10">6-10 vehicles</option>
                      <option value="11-25">11-25 vehicles</option>
                      <option value="26-50">26-50 vehicles</option>
                      <option value="50+">50+ vehicles</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="location" className="block text-sm font-semibold text-foreground mb-2">
                      Location *
                    </label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
                      placeholder="City, State"
                    />
                  </div>
                </div>
              </div>

              {/* Date Selection */}
              <div>
                <label htmlFor="date" className="block text-sm font-semibold text-foreground mb-2">
                  Preferred Date *
                </label>
                <input
                  type="date"
                  id="date"
                  value={selectedDate}
                  onChange={handleDateChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200"
                />
              </div>

              {/* Time Slot Selection */}
              <div>
                <label className="block text-sm font-semibold text-foreground mb-3">
                  Preferred Time *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {timeSlots.map((slot) => (
                    <button
                      key={slot.id}
                      type="button"
                      onClick={() => slot.available && handleTimeSelect(slot.time)}
                      disabled={!slot.available}
                      className={`px-4 py-3 rounded-md text-sm font-medium transition-all duration-200 ${
                        selectedTime === slot.time
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : slot.available
                          ? 'bg-background border border-border text-foreground hover:border-primary'
                          : 'bg-muted text-muted-foreground cursor-not-allowed'
                      }`}
                    >
                      {slot.time}
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional Notes */}
              <div>
                <label htmlFor="notes" className="block text-sm font-semibold text-foreground mb-2">
                  Additional Notes
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200 resize-none"
                  placeholder="Any specific requirements or questions about your fleet..."
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full px-6 py-4 bg-cta text-cta-foreground font-semibold rounded-md hover:bg-opacity-90 transition-all duration-200 ease-out hover:-translate-y-0.5 shadow-brand flex items-center justify-center gap-2"
              >
                <Icon name="CalendarIcon" size={20} variant="solid" />
                Schedule Evaluation
              </button>

              <p className="text-xs text-muted-foreground text-center">
                By submitting this form, you agree to be contacted by our team to confirm your appointment details.
              </p>
            </form>
          </div>

          {/* Information Sidebar */}
          <div className="space-y-6">
            {/* What to Expect */}
            <div className="bg-card rounded-lg p-6 shadow-brand">
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 bg-primary rounded-lg flex-shrink-0">
                  <Icon name="ClipboardDocumentListIcon" size={24} variant="solid" className="text-primary-foreground" />
                </div>
                <div>
                  <h4 className="text-lg font-bold font-headline text-foreground mb-2">
                    What to Expect
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Our certified appraisers will arrive at your location with all necessary equipment to conduct a thorough assessment of your fleet.
                  </p>
                </div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success mt-1 flex-shrink-0" />
                  <span className="text-sm text-foreground">Professional, uniformed appraisers</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success mt-1 flex-shrink-0" />
                  <span className="text-sm text-foreground">Comprehensive vehicle inspection</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success mt-1 flex-shrink-0" />
                  <span className="text-sm text-foreground">Photo documentation process</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="CheckCircleIcon" size={16} variant="solid" className="text-success mt-1 flex-shrink-0" />
                  <span className="text-sm text-foreground">Immediate preliminary feedback</span>
                </li>
              </ul>
            </div>

            {/* Preparation Tips */}
            <div className="bg-card rounded-lg p-6 shadow-brand">
              <div className="flex items-start gap-3 mb-4">
                <div className="p-2 bg-accent rounded-lg flex-shrink-0">
                  <Icon name="LightBulbIcon" size={24} variant="solid" className="text-accent-foreground" />
                </div>
                <div>
                  <h4 className="text-lg font-bold font-headline text-foreground mb-2">
                    Preparation Tips
                  </h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Help us provide the most accurate valuation by having these items ready:
                  </p>
                </div>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <Icon name="DocumentTextIcon" size={16} variant="solid" className="text-primary mt-1 flex-shrink-0" />
                  <span className="text-sm text-foreground">Vehicle titles and registration</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="DocumentTextIcon" size={16} variant="solid" className="text-primary mt-1 flex-shrink-0" />
                  <span className="text-sm text-foreground">Maintenance and service records</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="DocumentTextIcon" size={16} variant="solid" className="text-primary mt-1 flex-shrink-0" />
                  <span className="text-sm text-foreground">Warranty information (if applicable)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="DocumentTextIcon" size={16} variant="solid" className="text-primary mt-1 flex-shrink-0" />
                  <span className="text-sm text-foreground">List of known issues or repairs</span>
                </li>
              </ul>
            </div>

            {/* Contact Information */}
            <div className="bg-primary text-primary-foreground rounded-lg p-6">
              <h4 className="text-lg font-bold font-headline mb-4">
                Need Immediate Assistance?
              </h4>
              <div className="space-y-3">
                <a
                  href="tel:+15551234567"
                  className="flex items-center gap-3 p-3 bg-primary-foreground/10 rounded-md hover:bg-primary-foreground/20 transition-colors duration-200"
                >
                  <Icon name="PhoneIcon" size={20} variant="solid" />
                  <div>
                    <p className="text-xs text-primary-foreground/80">Call Us</p>
                    <p className="text-sm font-semibold">(555) 123-4567</p>
                  </div>
                </a>
                <a
                  href="mailto:evaluations@fleetdirect.com"
                  className="flex items-center gap-3 p-3 bg-primary-foreground/10 rounded-md hover:bg-primary-foreground/20 transition-colors duration-200"
                >
                  <Icon name="EnvelopeIcon" size={20} variant="solid" />
                  <div>
                    <p className="text-xs text-primary-foreground/80">Email Us</p>
                    <p className="text-sm font-semibold">evaluations@fleetdirect.com</p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SchedulingTool;